import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';

type FaqTopic = 'acesso' | 'aplicacoes' | 'favoritos' | 'suporte';

interface FaqItem {
  id: string;
  topic: FaqTopic;
  question: string;
  answer: string;
}

interface FaqGroup {
  topic: FaqTopic;
  label: string;
  items: FaqItem[];
}

const TOPIC_LABELS: Record<FaqTopic, string> = {
  acesso: 'Acesso e login',
  aplicacoes: 'Aplicações',
  favoritos: 'Favoritos',
  suporte: 'Suporte'
};

const FAQS: FaqItem[] = [
  {
    id: 'primeiro-acesso',
    topic: 'acesso',
    question: 'Como faço para acessar o portal pela primeira vez?',
    answer:
      'Informe o usuário recebido da Michelin na tela de login e siga as etapas. No primeiro acesso, o portal pede que você defina uma nova senha antes de continuar.'
  },
  {
    id: 'esqueci-senha',
    topic: 'acesso',
    question: 'Esqueci minha senha. O que devo fazer?',
    answer: 'Na tela de login, informe seu usuário e clique em "Esqueci a senha" para receber as instruções de redefinição.'
  },
  {
    id: 'nao-consigo-entrar',
    topic: 'acesso',
    question: 'Por que não consigo entrar com meu usuário?',
    answer:
      'Confira se o usuário e a senha estão corretos. Colaboradores Michelin são direcionados ao login corporativo (SSO). Se o problema continuar, fale com a Michelin.'
  },
  {
    id: 'encontrar-aplicacao',
    topic: 'aplicacoes',
    question: 'Como encontro uma aplicação específica?',
    answer: 'Na página inicial, a seção Aplicações lista todas as aplicações liberadas para o seu usuário, identificadas pela sigla e pelo nome.'
  },
  {
    id: 'siglas',
    topic: 'aplicacoes',
    question: 'O que significam as siglas DRE, TLD e BIB?',
    answer: 'DRE é o Demonstrativo de Resultado do Exercício, TLD é o Transporte de Longa Distância e BIB é a Bibnet.'
  },
  {
    id: 'aplicacao-nao-aparece',
    topic: 'aplicacoes',
    question: 'Preciso de acesso a uma aplicação que não aparece para mim.',
    answer: 'As aplicações exibidas dependem das permissões do seu usuário. Solicite o acesso pelo Fale com a Michelin, informando a aplicação desejada.'
  },
  {
    id: 'adicionar-favorito',
    topic: 'favoritos',
    question: 'Como adiciono uma aplicação aos favoritos?',
    answer: 'Clique na estrela no canto do card da aplicação. Ela fica amarela e a aplicação passa a aparecer na seção Favoritos.'
  },
  {
    id: 'remover-favorito',
    topic: 'favoritos',
    question: 'Posso reordenar ou remover meus favoritos?',
    answer: 'Para remover, clique novamente na estrela, no card ou na seção Favoritos. A ordem segue a lista de aplicações e ainda não pode ser alterada manualmente.'
  },
  {
    id: 'falar-suporte',
    topic: 'suporte',
    question: 'Como falo com o suporte da Michelin?',
    answer: 'Use o item Fale com a Michelin no menu lateral para escolher o assunto e abrir sua solicitação.'
  },
  {
    id: 'video-nao-carrega',
    topic: 'suporte',
    question: 'O vídeo da página inicial não carrega. E agora?',
    answer: 'O vídeo é exibido pelo YouTube. Verifique se a sua rede permite o acesso ao YouTube e recarregue a página. O restante do portal funciona normalmente.'
  }
];

const normalize = (text: string): string =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

@Component({
  standalone: true,
  selector: 'app-faq-dialog',
  templateUrl: './faq-dialog.component.html',
  styleUrls: ['./faq-dialog.component.scss'],
  imports: [CommonModule, RouterLink, MatDialogModule, MatIconModule]
})
export class FaqDialogComponent {
  readonly topics = Object.keys(TOPIC_LABELS) as FaqTopic[];
  readonly topicLabels = TOPIC_LABELS;

  query = '';
  activeTopic: FaqTopic | null = null;
  expanded = new Set<string>();

  // Recomputed only on input/chip changes, never from the template.
  groups: FaqGroup[] = [];
  visibleCount = 0;
  topicCounts: Record<FaqTopic, number> = { acesso: 0, aplicacoes: 0, favoritos: 0, suporte: 0 };
  totalMatches = 0;

  constructor() {
    this.applyFilters();
  }

  get allExpanded(): boolean {
    return this.visibleCount > 0 && this.groups.every(group => group.items.every(item => this.expanded.has(item.id)));
  }

  onSearch(value: string): void {
    this.query = value;
    this.applyFilters();
  }

  clearSearch(): void {
    this.onSearch('');
  }

  selectTopic(topic: FaqTopic | null): void {
    this.activeTopic = topic;
    this.applyFilters();
  }

  toggle(item: FaqItem): void {
    if (this.expanded.has(item.id)) {
      this.expanded.delete(item.id);
    } else {
      this.expanded.add(item.id);
    }
  }

  toggleAll(): void {
    const visibleIds = this.groups.flatMap(group => group.items.map(item => item.id));
    if (this.allExpanded) {
      visibleIds.forEach(id => this.expanded.delete(id));
    } else {
      visibleIds.forEach(id => this.expanded.add(id));
    }
  }

  private applyFilters(): void {
    const term = normalize(this.query.trim());
    const matches = term
      ? FAQS.filter(item => normalize(`${item.question} ${item.answer} ${TOPIC_LABELS[item.topic]}`).includes(term))
      : FAQS;

    this.totalMatches = matches.length;
    this.topicCounts = { acesso: 0, aplicacoes: 0, favoritos: 0, suporte: 0 };
    matches.forEach(item => this.topicCounts[item.topic]++);

    const visible = this.activeTopic ? matches.filter(item => item.topic === this.activeTopic) : matches;
    this.visibleCount = visible.length;
    this.groups = this.topics
      .map(topic => ({ topic, label: TOPIC_LABELS[topic], items: visible.filter(item => item.topic === topic) }))
      .filter(group => group.items.length > 0);
  }
}
